import { eachWeekOfInterval, isWithinInterval, startOfWeek } from 'date-fns';
import {
  SCHOOL_CLOSURES,
  SCHOOL_YEAR_END,
  SCHOOL_YEAR_START,
  type SchoolClosure,
} from '../data/school-year-2026-2027.ts';
import { addDays, parseLocalDate, toLocalISO } from './dates.ts';

/** Monday-based school days. The week grid never shows the weekend. */
export const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'] as const;
export type DayIndex = 0 | 1 | 2 | 3 | 4;

/**
 * Every Monday of the school year, as local ISO dates.
 *
 * Anchored on the year start rather than stepping by 7 from an arbitrary date,
 * so a start date that is not itself a Monday still produces correct weeks.
 */
export const ACADEMIC_WEEKS: readonly string[] = eachWeekOfInterval(
  { start: parseLocalDate(SCHOOL_YEAR_START), end: parseLocalDate(SCHOOL_YEAR_END) },
  { weekStartsOn: 1 },
).map(toLocalISO);

/** The closure covering `date`, or null on a normal school day. */
export function closureForDate(date: Date): SchoolClosure | null {
  return (
    SCHOOL_CLOSURES.find((c) =>
      isWithinInterval(date, {
        start: parseLocalDate(c.start),
        end: parseLocalDate(c.end),
      }),
    ) ?? null
  );
}

/** Convenience wrapper for callers holding an ISO string rather than a Date. */
export function closureForISO(iso: string): SchoolClosure | null {
  return closureForDate(parseLocalDate(iso));
}

/**
 * Distinct closure labels touching Monday-Friday of the given week,
 * joined for display: "Vacances d’automne" or "Armistice • Fête des morts".
 */
export function weekHolidayLabel(weekStartISO: string): string {
  const monday = parseLocalDate(weekStartISO);
  const labels: string[] = [];

  for (let i = 0; i < 5; i++) {
    const closure = closureForDate(addDays(monday, i));
    if (closure && !labels.includes(closure.label)) labels.push(closure.label);
  }

  return labels.join(' • ');
}

/** Index of the week containing `date`, clamped into the school year. */
export function weekIndexForDate(date: Date): number {
  // Matched on the Monday of the whole calendar week, not on Monday-Friday: a
  // Saturday or Sunday is still *this* week to the teacher, and matching only
  // school days used to send every weekend to the far end of the year.
  const monday = toLocalISO(startOfWeek(date, { weekStartsOn: 1 }));

  const found = ACADEMIC_WEEKS.indexOf(monday);
  if (found >= 0) return found;

  // Out-of-year dates: clamp to the nearest end of the year.
  return monday < ACADEMIC_WEEKS[0]! ? 0 : ACADEMIC_WEEKS.length - 1;
}

/** A date's place in the week grid. */
export interface SchoolDayPosition {
  /** ISO Monday, as lessons store it. */
  weekStart: string;
  day: DayIndex;
}

/**
 * Where a date sits in the week grid, or null when it sits nowhere.
 *
 * Null for a weekend and for anything outside the school year — unlike
 * `weekIndexForDate`, which clamps, because "which lesson is this?" has no
 * answer on a Sunday and must not be given a plausible wrong one.
 */
export function schoolDayPosition(date: Date): SchoolDayPosition | null {
  const monday = toLocalISO(startOfWeek(date, { weekStartsOn: 1 }));
  if (!ACADEMIC_WEEKS.includes(monday)) return null;

  const iso = toLocalISO(date);
  const day = DAYS.findIndex((_, i) => toLocalISO(addDays(parseLocalDate(monday), i)) === iso);
  return day < 0 ? null : { weekStart: monday, day: day as DayIndex };
}

/** `schoolDayPosition` for a typed ISO date, which may be blank or half-typed. */
export function schoolDayPositionISO(iso: string): SchoolDayPosition | null {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? schoolDayPosition(parseLocalDate(iso)) : null;
}

/** Clamp any number into a valid `ACADEMIC_WEEKS` index. */
export function clampWeekIndex(index: number): number {
  if (!Number.isFinite(index)) return 0;
  return Math.max(0, Math.min(ACADEMIC_WEEKS.length - 1, Math.trunc(index)));
}

/** The five Monday-to-Friday dates of a given week. */
export function weekDayDates(weekStartISO: string): Date[] {
  const monday = parseLocalDate(weekStartISO);
  return DAYS.map((_, i) => addDays(monday, i));
}
